import { useEffect, useRef } from 'react'
import { Lock } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { useRouter } from '@/routing/navigation'
import { AnalyticsEvents, track } from '@/lib/services/analytics'
import type { TrialLimitKind } from '@/lib/billing/trial-limits'
import {
  TRIAL_MAX_CHAT_MESSAGES_PER_ROOM,
  TRIAL_MAX_CHAT_ROOMS,
  TRIAL_MAX_STUDIES,
} from '@/lib/billing/trial-limits'

interface TrialLimitUpgradeModalProps {
  open: boolean
  onClose: () => void
  /** Which trial cap was reached — controls copy and analytics. */
  kind: TrialLimitKind
}

type DismissSource = 'button' | 'esc' | 'overlay'

interface KindConfig {
  title: string
  description: string
}

const KIND_CONFIG: Record<TrialLimitKind, KindConfig> = {
  rooms: {
    title: `You've reached your conversation limit`,
    description: `Your free trial includes up to ${TRIAL_MAX_CHAT_ROOMS} conversations. Upgrade your plan to keep talking to your twins.`,
  },
  messages: {
    title: `You've reached your message limit`,
    description: `Free trials include ${TRIAL_MAX_CHAT_MESSAGES_PER_ROOM} messages per conversation. Upgrade your plan to continue this conversation.`,
  },
  studies: {
    title: `You've reached your trial limit`,
    description: `Free trials include ${TRIAL_MAX_STUDIES} study. Your progress is saved — upgrade your plan to launch it.`,
  },
}

/**
 * Upgrade modal shown when a trial user hits an operational cap
 * (conversations / messages / studies).
 */
export function TrialLimitUpgradeModal({
  open,
  onClose,
  kind,
}: TrialLimitUpgradeModalProps) {
  const router = useRouter()
  const dismissSourceRef = useRef<DismissSource>('button')
  const viewedRef = useRef(false)
  const config = KIND_CONFIG[kind]

  useEffect(() => {
    if (!open) {
      viewedRef.current = false
      return
    }
    if (!viewedRef.current) {
      track(AnalyticsEvents.TRIAL_LIMIT_REACHED, { limit: kind })
      viewedRef.current = true
    }
  }, [open, kind])

  const handleUpgrade = () => {
    track(AnalyticsEvents.TRIAL_LIMIT_UPGRADE_PLANS_CLICKED, { limit: kind })
    router.push('/settings/billing/plans')
    onClose()
  }

  const dismissWith = (source: DismissSource) => {
    track(AnalyticsEvents.TRIAL_LIMIT_UPGRADE_DISMISSED, { source, limit: kind })
    onClose()
  }

  const handleOpenChange = (next: boolean) => {
    if (next) return
    dismissWith(dismissSourceRef.current)
    dismissSourceRef.current = 'button'
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-sm gap-0 overflow-hidden rounded-2xl bg-background p-0"
        data-testid={`trial-limit-modal-${kind}`}
        onEscapeKeyDown={() => { dismissSourceRef.current = 'esc' }}
        onPointerDownOutside={() => { dismissSourceRef.current = 'overlay' }}
      >
        <div className="flex flex-col gap-5">
          <DialogHeader className="flex flex-col gap-2 text-center items-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--cta))]/20 text-[hsl(var(--cta))]">
              <Lock className="h-5 w-5" strokeWidth={1.75} />
            </span>
            <DialogTitle className="font-display text-[22px] font-normal leading-tight tracking-tight text-foreground text-center">
              {config.title}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground text-center">
              {config.description}
            </DialogDescription>
          </DialogHeader>

          <Button
            variant="cta"
            onClick={handleUpgrade}
            className="w-full text-[16px] mt-1"
            data-testid="trial-limit-upgrade-pro-cta"
          >
            Upgrade plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
